import React from 'react'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'
import { ArrowDown2 } from 'iconsax-react'

import NoPermissionContainer from '@SonarCX/components/molecules/NoPermissionContainer'
import SettingsSection from '../../SettingsSection'
import Add from '../../Controls/Add'
import Dropdown from '@SonarCX/components/atoms/Dropdown'
import AddColor from '../../Controls/Add/AddColour'
import DraggableSetting from '../DraggableSetting'

import { getStatusObj } from './helpers'
import { doAlphabeticalSort, doDragDrop } from '../DraggableSetting/helpers'
import useAccess, { PERMS } from '@SonarCX/hooks/useAccess'
import { useSettingsEdit } from '@SonarCX/providers/SettingsEditProvider'
import { AddErrorAlert, AddSuccessAlert } from '@SonarCX/slices/alert'
import { useStats } from '@SonarCX/providers/StatsProvider'
import { DEFAULT_COLORS } from '../../Controls/Add/helpers'
import {
  createStatus,
  deleteStatus,
  fetchStatus,
  updateStatus,
  patchStatus
} from '@SonarCX/slices/taskStatus'
import useStyles from './styles'

const StatusSettings = () => {
  const dispatch = useAppDispatch()

  const {
    statuses,
    loaded: taskStatusLoaded,
    loading: taskStatusLoading,
    error
  } = useAppSelector(storeState => storeState.taskStatus)

  const { setEditObject } = useSettingsEdit()
  const { globalTasks, globalLoaded } = useStats()
  const hasPermission = useAccess()

  const [saving, setSaving] = React.useState(false)
  const [color, setColor] = React.useState(null)
  const [base, setBase] = React.useState(null)

  const classes = useStyles()

  const handleAddStatus = React.useCallback(
    async name => {
      try {
        setSaving(true)
        const randomColor =
          DEFAULT_COLORS[
            Math.floor(Math.random() * DEFAULT_COLORS.length)
          ].toLowerCase()

        await dispatch(
          createStatus({
            name: name,
            colour: color ? color.toLowerCase() : randomColor,
            base_status: base ? base.toLowerCase() : null,
            system: false,
            flow_order: null
          })
        )
        dispatch(AddSuccessAlert('Changes saved'))
        setColor(null)
        setBase(null)
      } catch (err) {
        console.error(err)
        dispatch(AddErrorAlert({ err }))
      } finally {
        setSaving(false)
      }
    },
    [color, base, dispatch]
  )

  const handleDeleteStatus = React.useCallback(
    obj => {
      try {
        dispatch(deleteStatus(obj.id))
        dispatch(AddSuccessAlert('Changes saved'))
      } catch (err) {
        console.error(err)
        dispatch(AddErrorAlert({ err }))
      }
    },
    [dispatch]
  )

  const handleEditStatus = React.useCallback(
    async save => {
      //transform object
      save.base_status = save.base_status.toLowerCase()
      save.colour = save.colour?.toLowerCase() ?? null
      save.colour =
        save.colour && save.colour[0] === '#'
          ? save.colour.substring(1)
          : save.colour

      delete save.system
      delete save.dispName

      await dispatch(updateStatus(save))
      dispatch(AddSuccessAlert('Changes saved'))
      // resetting object after save
      setEditObject(null)
    },
    [setEditObject, dispatch]
  )

  const handleDragDrop = (
    sourceID: string,
    targetID: string,
    after?: boolean
  ) => {
    const toUpdate = doDragDrop(statuses, sourceID, targetID, after)
    dispatch(patchStatus(toUpdate))
  }

  const handleSort = React.useCallback(
    (ascending: boolean) => {
      dispatch(patchStatus(doAlphabeticalSort(ascending, statuses)))
    },
    [statuses, dispatch]
  )

  React.useEffect(() => {
    if (!taskStatusLoaded && !taskStatusLoading) {
      dispatch(fetchStatus())
    }
  }, [taskStatusLoaded, taskStatusLoading, dispatch])

  if (!hasPermission(PERMS.TasksAdmin)) {
    return null
  }

  return (
    <NoPermissionContainer error={error} skipAgentPage>
      <SettingsSection
        title='Statuses'
        loaded={taskStatusLoaded && globalLoaded}
        sort={handleSort}
      >
        {statuses.map((stat, i) => {
          const { count, ...obj } = getStatusObj(stat, classes, globalTasks)

          return (
            <DraggableSetting
              key={stat.id}
              obj={obj}
              handleDelete={handleDeleteStatus}
              handleEdit={handleEditStatus}
              count={count}
              isLast={i === statuses.length - 1}
              index={i}
              onDragDrop={handleDragDrop}
              objType='status'
              superType='task'
            />
          )
        })}

        <Add
          superType='task'
          type='status'
          handleAdd={handleAddStatus}
          saving={saving}
        >
          <AddColor saveColor={setColor} prevColor={color} />

          <Dropdown
            options={['Open', 'Completed']}
            selected={base ?? 'Open'}
            onSelect={val => setBase(val)}
            hideAdd
            hideClose
            stringOnly
          >
            <div className={classes.root}>
              <span style={{ paddingRight: '1rem' }}>{base ?? 'Open'}</span>

              <ArrowDown2 className={classes.icon} />
            </div>
          </Dropdown>
        </Add>
      </SettingsSection>
    </NoPermissionContainer>
  )
}

export default StatusSettings
